import { db } from '../firebase';
import { 
  collection, 
  doc, 
  query, 
  orderBy, 
  limit as limitQuery, 
  startAfter,
  where,
  getDocs, 
  addDoc, 
  deleteDoc, 
  updateDoc,
  getCountFromServer,
  getDoc
} from 'firebase/firestore';
import { validatePlayerName } from '../utils/contentModeration';

export const leaderboardService = {
  async getTopScores(gameId, limit = 10, startAfterDoc = null) {
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
      
      let queryRef = query(
        leaderboardRef,
        orderBy('score', 'desc'),
        limitQuery(limit)
      );

      if (startAfterDoc) {
        queryRef = query(queryRef, startAfter(startAfterDoc));
      }

      const snapshot = await getDocs(queryRef);
      const entries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return {
        entries,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limit
      };
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  },

  // Check if user already has an entry (by userId or name+email combo)
  async findExistingEntry(gameId, userId, name, email) {
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
      
      // First try to find by userId
      if (userId) {
        const userQuery = query(
          leaderboardRef,
          where('userId', '==', userId),
          limitQuery(1)
        );
        const userSnapshot = await getDocs(userQuery);
        if (!userSnapshot.empty) {
          const doc = userSnapshot.docs[0];
          return { id: doc.id, ...doc.data() };
        }
      }
      
      // Fallback: try to find by name and email combination
      if (name && email) {
        const nameEmailQuery = query(
          leaderboardRef,
          where('name', '==', name.trim()),
          where('email', '==', email.trim()),
          limitQuery(1)
        );
        const nameEmailSnapshot = await getDocs(nameEmailQuery);
        if (!nameEmailSnapshot.empty) {
          const doc = nameEmailSnapshot.docs[0];
          return { id: doc.id, ...doc.data() };
        }
      }
      
      // Last resort: find by name only (less reliable)
      if (name) {
        const nameQuery = query(
          leaderboardRef,
          where('name', '==', name.trim()),
          limitQuery(3) // Get a few to check timestamps
        );
        const nameSnapshot = await getDocs(nameQuery);
        
        // If we find entries with the same name, return the most recent one
        if (!nameSnapshot.empty) {
          const entries = nameSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          // Sort by timestamp and return the most recent
          entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          return entries[0];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding existing entry:', error);
      return null;
    }
  },

  async addOrUpdateScore(gameId, scoreData) {
    try {
      // Validate input data
      if (scoreData.name) {
        const validation = validatePlayerName(scoreData.name);
        if (!validation.isValid) {
          throw new Error(`Invalid name: ${validation.reason}`);
        }
      }

      if (typeof scoreData.score !== 'number' || scoreData.score < 0) {
        throw new Error('Invalid score value');
      }

      if (scoreData.email && scoreData.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(scoreData.email.trim())) {
          throw new Error('Invalid email format');
        }
      }

      const trimmedName = scoreData.name.trim();
      const trimmedEmail = scoreData.email ? scoreData.email.trim() : '';
      
      // Check for existing entry
      const existingEntry = await this.findExistingEntry(
        gameId, 
        scoreData.userId, 
        trimmedName, 
        trimmedEmail
      );
      
      if (existingEntry) {
        // Update existing entry if new score is better
        const isLowerBetter = scoreData.gameType === 'pindrop';
        const isNewBetter = isLowerBetter 
          ? scoreData.score < existingEntry.score 
          : scoreData.score > existingEntry.score;
        
        if (isNewBetter) {
          return await this.updateScore(gameId, existingEntry.id, {
            score: scoreData.score,
            name: trimmedName,
            email: trimmedEmail,
            userId: scoreData.userId || existingEntry.userId
          });
        } else {
          // Return existing entry if new score isn't better
          return existingEntry;
        }
      } else {
        // Create new entry
        const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
        const docRef = await addDoc(leaderboardRef, {
          name: trimmedName,
          email: trimmedEmail,
          score: scoreData.score,
          userId: scoreData.userId || null,
          gameType: scoreData.gameType || null,
          timestamp: new Date().toISOString(),
          validated: true,
          moderationVersion: '1.0',
          createdAt: new Date().toISOString()
        });
        
        const newEntry = await getDoc(docRef);
        return { id: newEntry.id, ...newEntry.data() };
      }
    } catch (error) {
      console.error('Error adding/updating score:', error);
      throw error;
    }
  },

  // Legacy method - now redirects to addOrUpdateScore
  async addScore(gameId, scoreData) {
    return this.addOrUpdateScore(gameId, scoreData);
  },

  async updateScore(gameId, recordId, updates) {
    try {
      // Validate player name if it's being updated
      if (updates.name) {
        const validation = validatePlayerName(updates.name);
        if (!validation.isValid) {
          throw new Error(`Invalid name: ${validation.reason}`);
        }
        updates.name = updates.name.trim(); // Ensure trimmed name
      }

      // Validate score if it's being updated
      if (updates.score !== undefined) {
        if (typeof updates.score !== 'number' || updates.score < 0) {
          throw new Error('Invalid score value');
        }
      }

      // Validate email if it's being updated
      if (updates.email !== undefined) {
        if (updates.email && updates.email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(updates.email.trim())) {
            throw new Error('Invalid email format');
          }
          updates.email = updates.email.trim();
        } else {
          updates.email = '';
        }
      }

      const docRef = doc(db, 'leaderboard', gameId, 'entries', recordId);
      await updateDoc(docRef, {
        ...updates,
        timestamp: new Date().toISOString(),
        lastValidated: new Date().toISOString(),
        moderationVersion: '1.0'
      });
      const updatedDoc = await getDoc(docRef);
      return { id: updatedDoc.id, ...updatedDoc.data() };
    } catch (error) {
      console.error('Error updating score:', error);
      throw error;
    }
  },

  async deleteEntry(gameId, entryId) {
    try {
      const docRef = doc(db, 'leaderboard', gameId, 'entries', entryId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting entry:', error);
      throw error;
    }
  },

  async getTotalEntries(gameId) {
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
      const snapshot = await getCountFromServer(leaderboardRef);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error fetching total leaderboard entries:', error);
      throw error;
    }
  },

  // OPTIMIZED VERSION - Only show top entries, no separate user entry
  async getTopScoresWithUserEntry(gameId, userRecordId, limit = 10) {
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
      
      // Get top scores (always fast - only reads 'limit' documents)
      const topScoresQuery = query(
        leaderboardRef,
        orderBy('score', 'desc'),
        limitQuery(limit)
      );

      const topScoresSnapshot = await getDocs(topScoresQuery);

      const topEntries = topScoresSnapshot.docs.map((doc, index) => ({
        id: doc.id,
        ...doc.data(),
        rank: index + 1, // Simple rank based on position in top results
        isUser: doc.id === userRecordId
      }));

      // Check if user is in top entries
      const userInTopTen = topEntries.some(entry => entry.id === userRecordId);

      return {
        topEntries,
        userEntry: null, // Never show separate user entry
        userRank: userInTopTen 
          ? topEntries.find(entry => entry.id === userRecordId)?.rank 
          : null // No rank if not in top 10
      };
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  },

  // Legacy method with expensive rank calculation - only use for small datasets
  async getTopScoresWithUserRank(gameId, userScore, userName, recordId) {
    try {
      // For large datasets, use the optimized version instead
      return this.getTopScoresWithUserEntry(gameId, recordId);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  },

  // REMOVED: Expensive rank calculation
  // This function was costing $5+ per call with large datasets
  // We only show top 10 now, so ranking is not needed
  async getApproximateRank(gameId, userScore, gameType = null) {
    // Always return null since we don't show ranks outside top 10
    return null;
  },

  // REMOVED: Expensive function that reads ALL documents
  // This could cost hundreds of dollars with 500K entries
  // Use getTopScores with pagination instead
  async getAllScores(gameId) {
    console.warn('getAllScores is deprecated - use getTopScores with pagination instead');
    // Return empty to prevent accidental expensive calls
    return { entries: [] };
  },
}; 